'use client';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions } from '@susu/custom-made/providers/reducers/global';
import { Level, ZoomPosition } from '@susu/custom-made/utils/constants';
import { actions as optionActions } from '@susu/custom-made/providers/reducers/option';
import { Section } from '@susu/custom-made/utils/navigation';
import cn from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';
import Cursor from '../cursor/cursor';
import styles from './preview.module.scss';
import { startAnimation } from '@susu/custom-made/utils/animation';
import { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { checkConfigurationTypeRotationAvailable, checkRotationStep } from '@susu/custom-made/utils/utils';
import PreviewFirstLayer from './previewFirstLayer';
import PreviewOtherLayers from './previewOtherLayers';
import { LayerDefinition } from '@susu/custom-made/utils/preview/layers';

export interface PreviewProps {
  presetIndex?: number;
  layers: LayerDefinition[];
  fallbackImage: LayerDefinition;
  isZoomable?: boolean;
  updateFailedLayers?: (layer: string) => void;
  configurationType?: ConfigurationTypes;
}

export default function Preview({
  presetIndex = 0,
  layers,
  fallbackImage,
  isZoomable,
  updateFailedLayers,
  configurationType,
}: Readonly<PreviewProps>) {
  const { dispatch, state } = useContext(ThemeContext);
  const [isPreviewFailed, setIsPreviewFailed] = useState(false);
  const zoomButton = useRef<HTMLButtonElement>(null);
  const resetButton = useRef<HTMLButtonElement>(null);
  const viewSize = useViewSize();
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [showCursor, setShowCursor] = useState(false);
  const zoomButtonInputRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const previewContainer = useRef<HTMLDivElement>(null);

  const clickToZoom = () => {
    zoomButton.current?.click();
  };

  const clickToReset = useCallback(() => {
    resetButton.current?.click();
  }, []);

  const panningStart = () => {
    setTimeout(() => {
      dispatch({
        type: actions.SET_PREVIEW_PANNING,
        payload: true,
      });
    }, 100);
  };

  const panningStop = () => {
    setTimeout(() => {
      dispatch({
        type: actions.SET_PREVIEW_PANNING,
        payload: false,
      });
    }, 100);
  };

  const dispatchZoomIn = () => {
    dispatch({
      type: actions.SET_PREVIEW_ZOOM,
      payload: true,
    });
  };

  const dispatchZoomOut = () => {
    dispatch({
      type: actions.SET_PREVIEW_ZOOM,
      payload: false,
    });
  };

  const checkScaleState = (ref: ReactZoomPanPinchRef) => {
    if (ref.state.scale === 1) {
      dispatchZoomOut();
    }
  };

  const mouseMove = (e: any) => {
    setMouseY(e.clientY + 15);
    setMouseX(e.clientX + 10);
  };

  const onAnimationEnd = () => {
    dispatch({
      type: optionActions.SET_ANIMATION_STATUS,
      payload: { status: false, from: undefined, to: undefined}
    });
  }

  useEffect(() => {
    if (state.animation.status && state.growthbookFeatures['show-3D-preview-image']) {
      if (state.growthbookFeatures['enable-rotation-animation']) {
        if(checkConfigurationTypeRotationAvailable(configurationType, state.growthbookFeatures['show-3D-preview-image'])) {
          const element = previewContainer.current;
          startAnimation({element, fromStep: checkRotationStep(state.animation.from).spriteStep, onAnimationEnd, toStep: checkRotationStep(state.animation.to).spriteStep, totalSteps: 19})
        }
      } else {
        onAnimationEnd();
      }
    }
  }, [state.animation.status]);

  useEffect(() => {
    if (state.section) {
      clickToReset();
    }
  }, [state.section, clickToReset]);

  useEffect(() => {
    if (
      state.selectedOption?.zoomPosition &&
      state.selectedOption?.zoomPosition > 0 &&
      state.level === Level.Subgroup &&
      state.section !== Section.Fabric
    ) {
      zoomButtonInputRefs.current[state.selectedOption?.zoomPosition]?.click();
    } else {
      clickToReset();
    }
  }, [state.selectedOption, clickToReset, state.level, state.section]);

  const Controls = () => {
    const { zoomIn, resetTransform, zoomToElement } = useControls();
    const { dispatch, state } = useContext(ThemeContext);

    const zoomInPreview = () => {
      if (
        state.isPreviewZoomed &&
        (viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet || !state.isPreviewPanning)
      ) {
        resetTransform();
        dispatchZoomOut();
      } else if (!state.isPreviewZoomed) {
        zoomIn();
        dispatchZoomIn();
      }
    };

    const zoomElement = (id: string) => {
      zoomToElement(id, 1.65);
      dispatchZoomIn();
    };

    const resetZoom = () => {
      resetTransform();
      dispatch({
        type: actions.SET_PREVIEW_ZOOM,
        payload: false,
      });
    };

    return (
      <>
        <button className={styles.clickableHiddenButton} onClick={zoomInPreview} ref={zoomButton}>
          Zoom
        </button>
        <button className={styles.clickableHiddenButton} onClick={resetZoom} ref={resetButton}>
          Reset
        </button>
        {Object.values(ZoomPosition).map(position => (
          <button
            key={`button_${position}`}
            className={styles.clickableHiddenButton}
            ref={ref => {
              zoomButtonInputRefs.current[position] = ref;
            }}
            onClick={() => {
              zoomElement(`zoomPanel_${presetIndex}_${position}`);
            }}
          ></button>
        ))}
      </>
    );
  };

  return (
    <div className={styles.previewContainer} data-test-id={'preview'} ref={previewContainer}>
      {isZoomable && (
        <TransformWrapper
          doubleClick={{ disabled: true }}
          onPanningStart={panningStart}
          onPanningStop={panningStop}
          disablePadding={true}
          onWheelStart={dispatchZoomIn}
          onPinchingStart={dispatchZoomIn}
          onWheelStop={checkScaleState}
          onPinchingStop={checkScaleState}
          maxScale={2}
        >
          <Controls />
          <div className={styles.cursorContainer}>
            <Cursor showCursor={showCursor} isZoomed={state.isPreviewZoomed} mouseX={mouseX} mouseY={mouseY} />
          </div>
          <TransformComponent>
            {Object.keys(ZoomPosition).map(position => (
              <div
                key={`div_${position}`}
                id={`zoomPanel_${presetIndex}_${ZoomPosition[position]}`}
                className={cn(styles.panelZoom, styles[`zoomPanel${position}`])}
              ></div>
            ))}
            <div
              className={cn(styles.preview, styles.isZoomable)}
              onClick={clickToZoom}
              onMouseMove={mouseMove}
              onMouseOver={() => {
                setShowCursor(true);
              }}
              onMouseOut={() => {
                setShowCursor(false);
              }}
            >
              <PreviewFirstLayer
                layers={layers}
                fallbackImage={fallbackImage}
                isPreviewFailed={isPreviewFailed}
                setIsPreviewFailed={setIsPreviewFailed}
                configurationType={configurationType}
              />
              {!isPreviewFailed && (
                <PreviewOtherLayers
                  layers={layers}
                  updateFailedLayers={updateFailedLayers}
                  configurationType={configurationType}
                />
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      )}
      {!isZoomable && (
        <div className={styles.preview}>
          <PreviewFirstLayer
            layers={layers}
            fallbackImage={fallbackImage}
            isPreviewFailed={isPreviewFailed}
            setIsPreviewFailed={setIsPreviewFailed}
            configurationType={configurationType}
          />
          {!isPreviewFailed && (
            <PreviewOtherLayers
              layers={layers}
              updateFailedLayers={updateFailedLayers}
              configurationType={configurationType}
            />
          )}
        </div>
      )}
    </div>
  );
}
